import React, { useState, useEffect } from 'react';
import { Plus, Users } from 'lucide-react';
import type { Organization } from '../../api/organizations';
import { organizationAPI as organizationService } from '../../api/organizations';
import { OrganizationCard } from './OrganizationCard';
import { OrganizationForm } from './OrganizationForm';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
// import { Badge } from '../ui/Badge/Badge'; // Not used
import Modal from '../Modal/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import styles from './OrganizationsList.module.css';

interface OrganizationsListProps {
  className?: string;
}

type SortOption = 'name' | 'created_at' | 'member_count' | 'plan_type';
type FilterOption = 'all' | Organization['plan_type'];

const PLAN_ORDER: Record<Organization['plan_type'], number> = {
  enterprise: 6,
  agency: 5,
  team: 4,
  pro: 3,
  professional: 3,
  starter: 2,
  free: 1,
};

export const OrganizationsList: React.FC<OrganizationsListProps> = ({ className = '' }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { /* user */ } = useAuth(); // user not used
  const { addToast } = useToast();

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    filterAndSortOrganizations();
  }, [organizations, searchTerm, sortBy, filterBy]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationService.getOrganizations();
      setOrganizations(data);
    } catch (err) {
      setError('Failed to load organizations');
      addToast('Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortOrganizations = () => {
    let filtered = [...organizations];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.slug.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply plan filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(org => org.plan_type === filterBy);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created_at':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'member_count':
          // Since member_count isn't available, use a default sort
          return 0;
        case 'plan_type':
          return PLAN_ORDER[b.plan_type] - PLAN_ORDER[a.plan_type];
        default:
          return 0;
      }
    });

    setFilteredOrganizations(filtered);
  };

  const handleCreateOrganization = async (data: any) => {
    try {
      setIsCreating(true);
      setCreateError(null); // Clear previous errors
      const newOrg = await organizationService.createOrganization(data);
      setOrganizations(prev => [...prev, newOrg]);
      setShowCreateModal(false);
      setCreateError(null); // Clear error on success
      addToast('Organization created successfully', 'success');
    } catch (err: any) {
      // Extract error message from API response
      const errorMessage = err.message || 'Failed to create organization';
      setCreateError(errorMessage);
      // Don't show toast - error is displayed in modal
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateOrganization = async (data: any) => {
    if (!selectedOrganization) return;

    try {
      setIsUpdating(true);
      const updatedOrg = await organizationService.updateOrganization(selectedOrganization.id, data);
      setOrganizations(prev => 
        prev.map(org => org.id === selectedOrganization.id ? updatedOrg : org)
      );
      setShowEditModal(false);
      setSelectedOrganization(null);
      addToast('Organization updated successfully', 'success');
    } catch (err) {
      addToast('Failed to update organization', 'error');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    try {
      await organizationService.deleteOrganization(orgId);
      setOrganizations(prev => prev.filter(org => org.id !== orgId));
      addToast('Organization deleted successfully', 'success');
    } catch (err) {
      addToast('Failed to delete organization', 'error');
    }
  };

  const openEditModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    setShowEditModal(true);
  };

  const getStatsForPlan = (planType: string) => {
    const count = organizations.filter(org => org.plan_type === planType).length;
    return count;
  };

  const getTotalMembers = () => {
    // Since member_count isn't available in the Organization type, return 0 for now
    return 0;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.skeleton}>
          <div className={styles.skeletonHeader}></div>
          <div className={styles.skeletonCards}>
            {[1, 2, 3].map(i => (
              <div key={i} className={styles.skeletonCard}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>
          <p>{error}</p>
          <Button onClick={loadOrganizations} variant="primary" size="sm">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.organizationsList} ${className}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Organizations</h1>
            <p className={styles.subtitle}>
              Manage your organizations and team access
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="primary"
            size="sm"
            className={styles.createButton}
          >
            <Plus className={styles.buttonIcon} />
            Create Organization
          </Button>
        </div>

        {/* Stats */}
        <div className={styles.stats}>
          <div className={styles.stat}>
            <div className={styles.statValue}>{organizations.length}</div>
            <div className={styles.statLabel}>Total Organizations</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{getTotalMembers()}</div>
            <div className={styles.statLabel}>Total Members</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{getStatsForPlan('enterprise')}</div>
            <div className={styles.statLabel}>Enterprise</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{getStatsForPlan('professional')}</div>
            <div className={styles.statLabel}>Professional</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <Users className={styles.searchIcon} /> {/* Using Users as placeholder */}
          <Input
            type="text"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterControls}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={styles.filterButton}
          >
            <Users className={styles.buttonIcon} /> {/* Using Users as placeholder */}
            Filters
          </Button>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className={styles.sortSelect}
          >
            <option value="name">Sort by Name</option>
            <option value="created_at">Sort by Created Date</option>
            <option value="member_count">Sort by Members</option>
            <option value="plan_type">Sort by Plan</option>
          </select>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className={styles.filtersPanel}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Plan Type</label>
            <div className={styles.filterOptions}>
              {[
                { value: 'all', label: 'All Plans' },
                { value: 'free', label: 'Free' },
                { value: 'starter', label: 'Starter' },
                { value: 'pro', label: 'Pro' },
                { value: 'agency', label: 'Agency' },
                { value: 'professional', label: 'Professional' },
                { value: 'team', label: 'Team' },
                { value: 'enterprise', label: 'Enterprise' },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setFilterBy(option.value as FilterOption)}
                  className={`${styles.filterOption} ${filterBy === option.value ? styles.filterOptionActive : ''}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Organizations Grid */}
      <div className={styles.organizationsGrid}>
        {filteredOrganizations.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateContent}>
              <Users className={styles.emptyStateIcon} />
              <h3 className={styles.emptyStateTitle}>
                {searchTerm || filterBy !== 'all' ? 'No organizations found' : 'No organizations yet'}
              </h3>
              <p className={styles.emptyStateDescription}>
                {searchTerm || filterBy !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by creating your first organization'
                }
              </p>
              {!searchTerm && filterBy === 'all' && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="primary"
                  size="sm"
                >
                  <Plus className={styles.buttonIcon} />
                  Create Organization
                </Button>
              )}
            </div>
          </div>
        ) : (
          filteredOrganizations.map((organization) => (
            <OrganizationCard
              key={organization.id}
              organization={organization}
              onEdit={openEditModal}
              onDelete={handleDeleteOrganization}
              currentUserRole={organization.user_role || 'member'}
              // className={styles.organizationCard} // Not supported by OrganizationCard
            />
          ))
        )}
      </div>

      {/* Create Organization Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateError(null); // Clear error when closing modal
        }}
        title="Create Organization"
        size="large"
      >
        <OrganizationForm
          mode="create"
          onSubmit={handleCreateOrganization}
          onCancel={() => {
            setShowCreateModal(false);
            setCreateError(null); // Clear error when canceling
          }}
          isLoading={isCreating}
          error={createError}
        />
      </Modal>

      {/* Edit Organization Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedOrganization(null);
        }}
        title="Edit Organization"
        size="large"
      >
        {selectedOrganization && (
          <OrganizationForm
            mode="edit"
            organization={selectedOrganization}
            onSubmit={handleUpdateOrganization}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedOrganization(null);
            }}
            isLoading={isUpdating}
          />
        )}
      </Modal>
    </div>
  );
}; 